<template>
  <div class="amount-picker">
    <h2 class="amount-picker__title">Scegli l’importo</h2>
    <div class="amount-picker__grid">
      <div
        v-for="amount in amounts"
        :key="amount"
        class="amount-picker__card"
        :class="{ 'amount-picker__card--selected': amount === selected_amount }"
      >
        <!--
          b-form-radio diretto invece del wrapper vb-radio, che non espone il valore
          rappresentato dal pallino e resterebbe sempre spento: le classi .vb-radio
          sono globali, come fa liveeps/detail/room/room-rate.vue. Il controllo è lo
          store: `checked` legge il taglio scelto, il commit lo fa @change
        -->
        <b-form-radio
          class="vb-radio vb-radio--light"
          name="gift-card-amount"
          :value="amount"
          :checked="selected_amount"
          @change="on_select(amount)"
        >
          <span>€{{ amount }}</span>
        </b-form-radio>
        <span v-if="amount === featured_amount" class="amount-picker__badge">Più richiesto</span>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapGetters, mapState } from 'vuex';

  // taglio segnalato come più richiesto e preselezionato all'apertura. Il
  // catalogo non ha questa nozione: è una scelta di vetrina, come nel Figma
  const FEATURED_AMOUNT = 100;

  export default {
    name: 'amount-picker',

    computed: {
      ...mapState('livegift', ['selected_amount']),
      ...mapGetters('livegift', ['selected_product_data']),

      featured_amount() {
        return FEATURED_AMOUNT;
      },

      // tagli vendibili del prodotto scelto, come li dichiara il catalogo. I falsy
      // li scarto qui: un taglio a zero non si mostra, così non finisce nemmeno
      // nella preselezione del watcher e nel carrello del checkout
      amounts() {
        return (this.selected_product_data.amounts || []).filter(Boolean);
      },
    },

    watch: {
      // i tagli arrivano col catalogo e cambiano col prodotto. Appena ci sono
      // preseleziono quello di vetrina, o il primo se questo prodotto non lo
      // vende; se il taglio già scelto è ancora in vendita non lo tocco
      amounts: {
        immediate: true,
        handler(amounts) {
          if (!amounts.length) return;
          if (amounts.indexOf(this.selected_amount) !== -1) return;

          const has_featured = amounts.indexOf(FEATURED_AMOUNT) !== -1;
          this.on_select(has_featured ? FEATURED_AMOUNT : amounts[0]);
        },
      },
    },

    methods: {
      on_select(value) {
        this.$store.commit('livegift/selected_amount', value);
      },
    },
  };
</script>

<style lang="scss" scoped>

  .amount-picker {
    // misure del pallino disegnate da vb-radio, servono per riposizionarlo
    // quando la label si prende il padding della card. In px perché vanno
    // sommate al padding: vb-radio le dichiara in rem (1.25rem / .625rem)
    $radio-size: $grid-gutter-20;
    $radio-dot-size: $grid-gutter-20 / 2;
    $radio-gap: $grid-gutter-width / 2;
    $card-padding: $grid-gutter-20;

    &__title {
      margin: 0 0 $grid-gutter-20;
      font-size: $font-size-base * 1.5;
      font-weight: $vb-font-weight-semibold;
      line-height: 32px;
      color: $gray-900;
    }

    &__grid {
      display: grid;
      gap: $grid-gutter-width / 2;
    }

    &__card {
      position: relative;
      background-color: $white;
      border: 2px solid $gray-300;
      border-radius: $border-radius-lg * 2;
      transition: border-color .15s ease, background-color .15s ease;

      &--selected {
        background-color: $selected;
        border-color: $info;
      }

      ::v-deep .custom-control {
        padding-left: 0;
      }

      ::v-deep .custom-control-label {
        display: block;
        width: 100%;
        padding: $card-padding $card-padding $card-padding ($card-padding + $radio-size + $radio-gap);
        cursor: pointer;

        &::before,
        &::after {
          top: 50%;
          transform: translateY(-50%);
        }

        &::before {
          left: $card-padding;
        }

        &::after {
          left: $card-padding + ($radio-size - $radio-dot-size) / 2;
        }
      }

      // l'importo è lo span dentro la label del radio
      ::v-deep .custom-control-label > span {
        margin-left: 0; // la distanza dal pallino è nel padding della label
        font-size: 24px;
        font-weight: $vb-font-weight-semibold;
        line-height: 28px;
        color: $gray-900;

        @include media-breakpoint-up(lg) {
          font-size: 28px;
        }
      }
    }

    &__badge {
      position: absolute;
      top: -12px;
      right: $grid-gutter-width / 2;
      padding: 2px 10px;
      font-size: $font-size-sm;
      font-weight: $vb-font-weight-semibold;
      line-height: 20px;
      color: $white;
      background-color: $info;
      border-radius: 100px;
      white-space: nowrap;
    }
  }
</style>